import { useEffect, useMemo, useState, useCallback, createContext, useContext, useReducer, createElement } from 'react';
import { Upload, isSupported } from 'tus-js-client';

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var createUpload = function createUpload(file, options, dispatchIsAborted) {
  var upload = new Upload(file, options);
  var originalStart = upload.start.bind(upload);
  var originalAbort = upload.abort.bind(upload);

  var start = function start() {
    originalStart();
    dispatchIsAborted(false);
  };

  var abort = function () {
    var _ref = _asyncToGenerator(regeneratorRuntime.mark(function _callee() {
      return regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              originalAbort();
              dispatchIsAborted(true);

            case 2:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function abort() {
      return _ref.apply(this, arguments);
    };
  }();

  upload.start = start;
  upload.abort = abort;
  return {
    upload: upload,
    originalStart: originalStart,
    originalAbort: originalAbort
  };
};

var startOrResumeUpload = function startOrResumeUpload(upload) {
  upload.findPreviousUploads().then(function (previousUploads) {
    if (previousUploads.length) {
      upload.resumeFromPreviousUpload(previousUploads[0]);
    }

    upload.start();
  });
};

var useAutoAbort = function useAutoAbort(upload, abort, autoAbort) {
  useEffect(function () {
    var abortUploading = function () {
      var _ref = _asyncToGenerator(regeneratorRuntime.mark(function _callee() {
        return regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!(!upload || !abort)) {
                  _context.next = 2;
                  break;
                }

                return _context.abrupt("return");

              case 2:
                _context.next = 4;
                return abort();

              case 4:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function abortUploading() {
        return _ref.apply(this, arguments);
      };
    }();

    return function () {
      if (!autoAbort) {
        return;
      }

      abortUploading();
    };
  }, [autoAbort, upload]);
};

var defaultUseTusOptionsValue = Object.freeze({
  autoAbort: true,
  autoStart: false
});
var useMergeTusOptions = function useMergeTusOptions(options) {
  return useMemo(function () {
    return _objectSpread2(_objectSpread2({}, defaultUseTusOptionsValue), options || {});
  }, [options]);
};

var initialUseTusState = Object.freeze({
  upload: undefined,
  isSuccess: false,
  isAborted: false,
  error: undefined,
  originalAbort: undefined
});
var useTus = function useTus(baseOption) {
  var _useMergeTusOptions = useMergeTusOptions(baseOption),
      autoAbort = _useMergeTusOptions.autoAbort,
      autoStart = _useMergeTusOptions.autoStart,
      uploadOptions = _useMergeTusOptions.uploadOptions;

  var _useState = useState(initialUseTusState),
      _useState2 = _slicedToArray(_useState, 2),
      tusState = _useState2[0],
      setTusState = _useState2[1];

  var updateTusState = useCallback(function (newOptions) {
    setTusState(function (tus) {
      return _objectSpread2(_objectSpread2({}, tus), newOptions);
    });
  }, []);
  var setUpload = useCallback(function (file) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    var targetOptions = _objectSpread2(_objectSpread2({}, uploadOptions), options);

    var onSuccess = function onSuccess() {
      var _targetOptions$onSucc;

      updateTusState({
        isSuccess: true
      });
      targetOptions === null || targetOptions === void 0 ? void 0 : (_targetOptions$onSucc = targetOptions.onSuccess) === null || _targetOptions$onSucc === void 0 ? void 0 : _targetOptions$onSucc.call(targetOptions);
    };

    var onError = function onError(error) {
      var _targetOptions$onErro;

      updateTusState({
        error: error
      });
      targetOptions === null || targetOptions === void 0 ? void 0 : (_targetOptions$onErro = targetOptions.onError) === null || _targetOptions$onErro === void 0 ? void 0 : _targetOptions$onErro.call(targetOptions, error);
    };

    var mergedUploadOptions = _objectSpread2(_objectSpread2({}, targetOptions), {}, {
      onSuccess: onSuccess,
      onError: onError
    });

    var dispatchIsAborted = function dispatchIsAborted(isAborted) {
      updateTusState({
        isAborted: isAborted
      });
    };

    var _createUpload = createUpload(file, mergedUploadOptions, dispatchIsAborted),
        upload = _createUpload.upload,
        originalAbort = _createUpload.originalAbort;

    if (autoStart) {
      startOrResumeUpload(upload);
    }

    updateTusState(_objectSpread2(_objectSpread2({}, initialUseTusState), {}, {
      upload: upload,
      originalAbort: originalAbort
    }));
  }, [autoStart, updateTusState, uploadOptions]);
  var remove = useCallback(function () {
    var _tusState$originalAbo;

    tusState === null || tusState === void 0 ? void 0 : (_tusState$originalAbo = tusState.originalAbort) === null || _tusState$originalAbo === void 0 ? void 0 : _tusState$originalAbo.call(tusState);
    setTusState(initialUseTusState);
  }, [tusState]);
  var tusResult = useMemo(function () {
    var _tusState$isSuccess, _tusState$isAborted;

    return {
      upload: tusState === null || tusState === void 0 ? void 0 : tusState.upload,
      isSuccess: (_tusState$isSuccess = tusState === null || tusState === void 0 ? void 0 : tusState.isSuccess) !== null && _tusState$isSuccess !== void 0 ? _tusState$isSuccess : false,
      error: tusState === null || tusState === void 0 ? void 0 : tusState.error,
      isAborted: (_tusState$isAborted = tusState === null || tusState === void 0 ? void 0 : tusState.isAborted) !== null && _tusState$isAborted !== void 0 ? _tusState$isAborted : false,
      setUpload: setUpload,
      remove: remove
    };
  }, [tusState, setUpload, remove]);
  useAutoAbort(tusResult.upload, tusState.originalAbort, autoAbort !== null && autoAbort !== void 0 ? autoAbort : false);
  return tusResult;
};

var ERROR_MESSAGES = {
  tusClientHasNotFounded: "No TusClient set, use TusClientProvider to set one",
  tusIsNotSupported: "This browser does not support uploads. Please use a modern browser instead."
};

var TusClientStateContext = createContext(undefined);
var TusClientDispatchContext = createContext(undefined);
var useTusClientState = function useTusClientState() {
  var tusClientState = useContext(TusClientStateContext);

  if (!tusClientState && process.env.NODE_ENV !== "production") {
    throw new Error(ERROR_MESSAGES.tusClientHasNotFounded);
  }

  return useMemo(function () {
    return tusClientState;
  }, [tusClientState]);
};
var useTusClientDispatch = function useTusClientDispatch() {
  var tusClientDispatch = useContext(TusClientDispatchContext);

  if (!tusClientDispatch && process.env.NODE_ENV !== "production") {
    throw new Error(ERROR_MESSAGES.tusClientHasNotFounded);
  }

  return useMemo(function () {
    return tusClientDispatch;
  }, [tusClientDispatch]);
};

var insertUploadInstance = function insertUploadInstance(cacheKey, upload) {
  return {
    type: "INSERT_UPLOAD_INSTANCE",
    payload: {
      cacheKey: cacheKey,
      uploadState: {
        upload: upload,
        isSuccess: false,
        isAborted: false
      }
    }
  };
};
var updateSuccessUpload = function updateSuccessUpload(cacheKey) {
  return {
    type: "UPDATE_SUCCESS_UPLOAD",
    payload: {
      cacheKey: cacheKey
    }
  };
};
var updateErrorUpload = function updateErrorUpload(cacheKey, error) {
  return {
    type: "UPDATE_ERROR_UPLOAD",
    payload: {
      cacheKey: cacheKey,
      error: error
    }
  };
};
var updateIsAbortedUpload = function updateIsAbortedUpload(cacheKey, isAborted) {
  return {
    type: "UPDATE_IS_ABORTED_UPLOAD",
    payload: {
      cacheKey: cacheKey,
      isAborted: isAborted
    }
  };
};
var removeUploadInstance = function removeUploadInstance(cacheKey) {
  return {
    type: "REMOVE_UPLOAD_INSTANCE",
    payload: {
      cacheKey: cacheKey
    }
  };
};
var updateDefaultOptions = function updateDefaultOptions(defaultOptions) {
  return {
    type: "UPDATE_DEFAULT_OPTIONS",
    payload: {
      defaultOptions: defaultOptions
    }
  };
};

var useTusStore = function useTusStore(cacheKey, baseOption) {
  var _tusResult$upload;

  var _useMergeTusOptions = useMergeTusOptions(baseOption),
      autoAbort = _useMergeTusOptions.autoAbort,
      autoStart = _useMergeTusOptions.autoStart,
      uploadOptions = _useMergeTusOptions.uploadOptions;

  var _useTusClientState = useTusClientState(),
      defaultOptions = _useTusClientState.defaultOptions,
      uploads = _useTusClientState.uploads;

  var tusClientDispatch = useTusClientDispatch();
  var setUpload = useCallback(function (file) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    var targetOptions = _objectSpread2(_objectSpread2(_objectSpread2({}, defaultOptions === null || defaultOptions === void 0 ? void 0 : defaultOptions(file)), uploadOptions), options);

    var onSuccess = function onSuccess() {
      var _targetOptions$onSucc;

      tusClientDispatch(updateSuccessUpload(cacheKey));
      targetOptions === null || targetOptions === void 0 ? void 0 : (_targetOptions$onSucc = targetOptions.onSuccess) === null || _targetOptions$onSucc === void 0 ? void 0 : _targetOptions$onSucc.call(targetOptions);
    };

    var onError = function onError(error) {
      var _targetOptions$onErro;

      tusClientDispatch(updateErrorUpload(cacheKey, error));
      targetOptions === null || targetOptions === void 0 ? void 0 : (_targetOptions$onErro = targetOptions.onError) === null || _targetOptions$onErro === void 0 ? void 0 : _targetOptions$onErro.call(targetOptions, error);
    };

    var mergedUploadOptions = _objectSpread2(_objectSpread2({}, targetOptions), {}, {
      onSuccess: onSuccess,
      onError: onError
    });

    var dispatchIsAborted = function dispatchIsAborted(isAborted) {
      tusClientDispatch(updateIsAbortedUpload(cacheKey, isAborted));
    };

    var _createUpload = createUpload(file, mergedUploadOptions, dispatchIsAborted),
        upload = _createUpload.upload;

    if (autoStart) {
      startOrResumeUpload(upload);
    }

    tusClientDispatch(insertUploadInstance(cacheKey, upload));
  }, [autoStart, cacheKey, defaultOptions, tusClientDispatch, uploadOptions]);
  var targetTusState = useMemo(function () {
    return uploads[cacheKey];
  }, [cacheKey, uploads]);
  var remove = useCallback(function () {
    var _targetTusState$uploa;

    targetTusState === null || targetTusState === void 0 ? void 0 : (_targetTusState$uploa = targetTusState.upload) === null || _targetTusState$uploa === void 0 ? void 0 : _targetTusState$uploa.abort();
    tusClientDispatch(removeUploadInstance(cacheKey));
  }, [targetTusState, tusClientDispatch, cacheKey]);
  var tusResult = useMemo(function () {
    var _targetTusState$isSuc, _targetTusState$isAbo;

    return {
      upload: targetTusState === null || targetTusState === void 0 ? void 0 : targetTusState.upload,
      isSuccess: (_targetTusState$isSuc = targetTusState === null || targetTusState === void 0 ? void 0 : targetTusState.isSuccess) !== null && _targetTusState$isSuc !== void 0 ? _targetTusState$isSuc : false,
      error: targetTusState === null || targetTusState === void 0 ? void 0 : targetTusState.error,
      isAborted: (_targetTusState$isAbo = targetTusState === null || targetTusState === void 0 ? void 0 : targetTusState.isAborted) !== null && _targetTusState$isAbo !== void 0 ? _targetTusState$isAbo : false,
      setUpload: setUpload,
      remove: remove
    };
  }, [targetTusState, setUpload, remove]);
  useAutoAbort(tusResult.upload, (_tusResult$upload = tusResult.upload) === null || _tusResult$upload === void 0 ? void 0 : _tusResult$upload.abort, autoAbort !== null && autoAbort !== void 0 ? autoAbort : false);
  return tusResult;
};

var tusClientReducer = function tusClientReducer(state, actions) {
  switch (actions.type) {
    case "INSERT_UPLOAD_INSTANCE":
      {
        var _actions$payload = actions.payload,
            _cacheKey = _actions$payload.cacheKey,
            uploadState = _actions$payload.uploadState;
        return _objectSpread2(_objectSpread2({}, state), {}, {
          uploads: _objectSpread2(_objectSpread2({}, state.uploads), {}, _defineProperty({}, _cacheKey, uploadState))
        });
      }

    case "UPDATE_SUCCESS_UPLOAD":
      {
        var _cacheKey2 = actions.payload.cacheKey;
        var target = state.uploads[_cacheKey2];

        if (!target) {
          return state;
        }

        return _objectSpread2(_objectSpread2({}, state), {}, {
          uploads: _objectSpread2(_objectSpread2({}, state.uploads), {}, _defineProperty({}, _cacheKey2, _objectSpread2(_objectSpread2({}, target || {}), {}, {
            isSuccess: true
          })))
        });
      }

    case "UPDATE_ERROR_UPLOAD":
      {
        var _actions$payload2 = actions.payload,
            _cacheKey3 = _actions$payload2.cacheKey,
            error = _actions$payload2.error;
        var _target = state.uploads[_cacheKey3];

        if (!_target) {
          return state;
        }

        return _objectSpread2(_objectSpread2({}, state), {}, {
          uploads: _objectSpread2(_objectSpread2({}, state.uploads), {}, _defineProperty({}, _cacheKey3, _objectSpread2(_objectSpread2({}, _target), {}, {
            error: error
          })))
        });
      }

    case "UPDATE_IS_ABORTED_UPLOAD":
      {
        var _actions$payload3 = actions.payload,
            _cacheKey4 = _actions$payload3.cacheKey,
            isAborted = _actions$payload3.isAborted;
        var _target2 = state.uploads[_cacheKey4];

        if (!_target2) {
          return state;
        }

        return _objectSpread2(_objectSpread2({}, state), {}, {
          uploads: _objectSpread2(_objectSpread2({}, state.uploads), {}, _defineProperty({}, _cacheKey4, _objectSpread2(_objectSpread2({}, _target2), {}, {
            isAborted: isAborted
          })))
        });
      }

    case "REMOVE_UPLOAD_INSTANCE":
      {
        var _cacheKey5 = actions.payload.cacheKey;
        var newUploads = state.uploads;
        delete newUploads[_cacheKey5];
        return _objectSpread2(_objectSpread2({}, state), {}, {
          uploads: newUploads
        });
      }

    case "UPDATE_DEFAULT_OPTIONS":
      {
        var defaultOptions = actions.payload.defaultOptions;
        return _objectSpread2(_objectSpread2({}, state), {}, {
          defaultOptions: defaultOptions
        });
      }

    default:
      return state;
  }
};
var tusClientInitialState = {
  uploads: {},
  defaultOptions: undefined
};

var TusClientProvider = function TusClientProvider(_ref) {
  var defaultOptions = _ref.defaultOptions,
      children = _ref.children;

  var _useReducer = useReducer(tusClientReducer, _objectSpread2(_objectSpread2({}, tusClientInitialState), {}, {
    defaultOptions: defaultOptions
  })),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      tusClientState = _useReducer2[0],
      tusClientDispatch = _useReducer2[1];

  useEffect(function () {
    if (isSupported || process.env.NODE_ENV === "production") {
      return;
    }

    console.error(ERROR_MESSAGES.tusIsNotSupported);
  }, []);
  useEffect(function () {
    if (tusClientState.defaultOptions === defaultOptions) {
      return;
    }

    tusClientDispatch(updateDefaultOptions(defaultOptions));
  }, [defaultOptions, tusClientState.defaultOptions]);
  var tusClientDispatchContextProviderElement = createElement(TusClientDispatchContext.Provider, {
    value: tusClientDispatch
  }, children);
  return createElement(TusClientStateContext.Provider, {
    value: tusClientState
  }, tusClientDispatchContextProviderElement);
};

export { TusClientProvider, useTus, useTusStore };
